import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Popover,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PiFlask } from 'react-icons/pi';
import { useSimulateSessionWebhook } from '@/models/payers';

export const SIMULATABLE_KYC_STATUSES = ['pending', 'in_progress', 'retry'];

const APPLICANT_ID_KEY = 'sumsub-simulate-applicant-id';

function useSimulateForm(sessionId: number, onDone?: () => void) {
  const simulate = useSimulateSessionWebhook();
  const [applicantId, setApplicantId] = useState(
    () => localStorage.getItem(APPLICANT_ID_KEY) ?? '',
  );

  const submit = () => {
    const trimmed = applicantId.trim();
    if (!trimmed) return;
    localStorage.setItem(APPLICANT_ID_KEY, trimmed);
    simulate.mutate(
      { sessionId, applicantId: trimmed },
      {
        onSuccess: () => {
          onDone?.();
          notifications.show({
            color: 'teal',
            message: 'Webhook queued — the session will update shortly.',
          });
        },
        onError: (err) =>
          notifications.show({ color: 'red', message: String(err) }),
      },
    );
  };

  return { applicantId, setApplicantId, submit, isPending: simulate.isPending };
}

function SimulateFields({
  sessionId,
  onDone,
}: {
  sessionId: number;
  onDone?: () => void;
}) {
  const { applicantId, setApplicantId, submit, isPending } = useSimulateForm(
    sessionId,
    onDone,
  );

  return (
    <Stack gap="xs">
      <Text fz="xs" fw={500}>
        Simulate SumSub webhook
      </Text>
      <TextInput
        size="xs"
        label="SumSub Applicant ID"
        placeholder="Enter applicant ID"
        value={applicantId}
        onChange={(e) => setApplicantId(e.target.value)}
      />
      <Button
        size="xs"
        loading={isPending}
        disabled={!applicantId.trim()}
        onClick={submit}
      >
        Simulate Webhook
      </Button>
    </Stack>
  );
}

export default function SimulateSumsubWebhook({
  sessionId,
  variant = 'icon',
}: {
  sessionId: number;
  variant?: 'icon' | 'inline';
}) {
  const [opened, setOpened] = useState(false);

  if (variant === 'inline') return <SimulateFields sessionId={sessionId} />;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={280}
      position="bottom-start"
      withArrow
      trapFocus
    >
      <Popover.Target>
        <Tooltip label="Simulate SumSub webhook (dev)">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="grape"
            onClick={() => setOpened((o) => !o)}
          >
            <PiFlask size={14} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <SimulateFields sessionId={sessionId} onDone={() => setOpened(false)} />
      </Popover.Dropdown>
    </Popover>
  );
}
